// FR-RD14: a session actively owned by a live RecorderPage must never also be picked up by a
// recovery sweep (the on-launch run, or a later online/visibility/backoff retrigger — see
// RecoveryProvider.tsx). Both would construct their own UploadQueue against the same
// grackleId; whichever calls `/complete` first wins and the other's part PUTs start 404ing,
// which leaves the live recording stuck at "Uploading" (found by the offline e2e scenario).
//
// Two layers:
//   - an in-memory Set for this tab/process (synchronous, always available), and
//   - a Web Lock per session (`navigator.locks`) so a sweep in ANOTHER tab of the same origin
//     also skips it. The browser releases a tab's locks when that tab closes or crashes,
//     which is exactly the moment the session becomes recoverable. Where the Web Locks API is
//     missing (old webviews, jsdom), only the in-process guard applies.
const LOCK_PREFIX = "grackle-recording:";

const active = new Set<string>();
const releasers = new Map<string, () => void>();

function lockManager(): LockManager | null {
  if (typeof navigator === "undefined") return null;
  return navigator.locks ?? null;
}

export function markSessionActive(id: string): void {
  active.add(id);
  const locks = lockManager();
  if (!locks || releasers.has(id)) return;
  let release: () => void = () => {};
  const held = new Promise<void>((resolve) => {
    release = resolve;
  });
  releasers.set(id, release);
  // Held until markSessionInactive (or until the tab dies). Best-effort: a failure here only
  // loses the cross-tab guard, never the recording.
  locks.request(LOCK_PREFIX + id, () => held).catch(() => {});
}

export function markSessionInactive(id: string): void {
  active.delete(id);
  const release = releasers.get(id);
  releasers.delete(id);
  release?.();
}

export function isSessionActive(id: string): boolean {
  return active.has(id);
}

/** Session ids a live recorder in any tab of this origin (this one included) currently holds.
 * Empty when the Web Locks API is unavailable. */
export async function sessionsLockedByAnyTab(): Promise<Set<string>> {
  const locks = lockManager();
  if (!locks) return new Set();
  try {
    const snapshot = await locks.query();
    const names = [...(snapshot.held ?? []), ...(snapshot.pending ?? [])].map((l) => l.name ?? "");
    return new Set(names.filter((n) => n.startsWith(LOCK_PREFIX)).map((n) => n.slice(LOCK_PREFIX.length)));
  } catch {
    return new Set();
  }
}
